if (!customElements.get('quantity-bundle-selector')) {
  class QuantityBundleSelector extends HTMLElement {
    constructor() {
      super();
      this._onRadioChange = this.onRadioChange.bind(this);
      this._onBundleSyncAtcPrice = () => this.scheduleSyncAtcPrice();
      this._onVariantChange = this.onVariantChange.bind(this);
      this._variantChangeUnsubscriber = null;
    }

    connectedCallback() {
      this.formId = this.dataset.formId;
      this.sectionId = this.dataset.sectionId;

      this.addEventListener('change', this._onRadioChange);
      document.addEventListener('bundle:sync-atc-price', this._onBundleSyncAtcPrice);
      this.subscribeToVariantChange();

      this.updateSelectedState();

      const checkedRadio = this.getCheckedRadio();
      if (checkedRadio) {
        this.updateFormQuantity(parseInt(checkedRadio.value, 10));
      }

      this.scheduleSyncAtcPrice();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this._onRadioChange);
      document.removeEventListener('bundle:sync-atc-price', this._onBundleSyncAtcPrice);
      this._variantChangeUnsubscriber?.();
    }

    scheduleSyncAtcPrice() {
      this.syncAtcPrice();
      requestAnimationFrame(() => this.syncAtcPrice());
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.syncAtcPrice(), { once: true });
      }
    }

    subscribeToVariantChange() {
      if (!window.FoxTheme?.pubsub?.subscribe) return;

      this._variantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
        this._onVariantChange
      );
    }

    onVariantChange(event) {
      const { sectionId, variant } = event.data || {};
      if (!variant?.price) return;
      if (this.sectionId && sectionId && this.sectionId !== sectionId) return;

      this.recalculateRadioPrices(variant.price);
      this.scheduleSyncAtcPrice();
    }

    recalculateRadioPrices(basePriceCents) {
      const discount2x = parseFloat(this.dataset.discount2x) || 0;
      const discount3x = parseFloat(this.dataset.discount3x) || 0;
      const tier3UsesDiscount = this.dataset.bundleTier3Discount === 'true';
      const base = basePriceCents;

      const price1x = base;
      const original2x = base * 2;
      const price2x = original2x - Math.round(original2x * (discount2x / 100));

      let price3x;
      if (tier3UsesDiscount) {
        const original3x = base * 3;
        price3x = original3x - Math.round(original3x * (discount3x / 100));
      } else {
        price3x = base * 3;
      }

      const prices = [price1x, price2x, price3x];
      this.querySelectorAll('.quantity-bundle__radio').forEach((radio, index) => {
        const cents = prices[index];
        if (cents == null) return;
        radio.dataset.price = String(cents);
        const formatted = this.formatPriceFromCents(cents);
        if (formatted) radio.dataset.priceFormatted = formatted;
      });
    }

    getCheckedRadio() {
      return this.querySelector('.quantity-bundle__radio:checked');
    }

    formatPriceFromCents(cents) {
      const priceCents = parseInt(cents, 10);
      if (Number.isNaN(priceCents)) return '';

      try {
        if (window.FoxTheme?.Currency?.formatMoney && window.FoxTheme?.settings?.moneyFormat) {
          return FoxTheme.Currency.formatMoney(priceCents, FoxTheme.settings.moneyFormat);
        }
      } catch (e) {
        // Invalid money format — fall through to dataset / visible price
      }

      return '';
    }

    getVisibleOptionPrice(selectedRadio) {
      const option = selectedRadio?.closest('.quantity-bundle__option');
      const priceEl = option?.querySelector('.quantity-bundle__price');
      return priceEl?.textContent?.trim() || '';
    }

    getPriceFormatted(selectedRadio) {
      if (!selectedRadio) return '';

      const fromDataset = selectedRadio.dataset.priceFormatted;
      if (fromDataset) return fromDataset;

      const visiblePrice = this.getVisibleOptionPrice(selectedRadio);
      if (visiblePrice) return visiblePrice;

      const priceCents = parseInt(selectedRadio.dataset.price, 10);
      if (!Number.isNaN(priceCents)) {
        const formatted = this.formatPriceFromCents(priceCents);
        if (formatted) return formatted;
      }

      return '';
    }

    getAddToCartLabel() {
      if (window.FoxTheme?.variantStrings?.addToCart) {
        const temp = document.createElement('div');
        temp.innerHTML = FoxTheme.variantStrings.addToCart;
        return temp.textContent?.trim() || FoxTheme.variantStrings.addToCart;
      }
      return 'Add to cart';
    }

    syncAtcPrice() {
      const checkedRadio = this.getCheckedRadio();
      if (!checkedRadio) return;

      const priceFormatted = this.getPriceFormatted(checkedRadio);
      if (!priceFormatted) return;

      this.updateAtcButtonPrice(priceFormatted);
    }

    updateAtcButtonPrice(priceFormatted) {
      const label = this.getAddToCartLabel();
      const markup = `${label} - <span data-atc-price>${priceFormatted}</span>`;

      document.querySelectorAll('[data-atc-price]').forEach((span) => {
        span.textContent = priceFormatted;
      });

      const buttonSelectors = [];
      if (this.sectionId && !this.sectionId.includes('{')) {
        buttonSelectors.push(`#ProductSubmitButton-${this.sectionId}`);
      }
      buttonSelectors.push('.sticky-atc-bar [name="add"]', '.product-form__submit[name="add"]');

      buttonSelectors.forEach((selector) => {
        document.querySelectorAll(selector).forEach((button) => {
          if (button.disabled) return;
          const textSpan = button.querySelector(':scope > span');
          if (!textSpan) return;
          textSpan.innerHTML = markup;
        });
      });
    }

    onRadioChange(event) {
      const radio = event.target.closest?.('.quantity-bundle__radio');
      if (!radio || !this.contains(radio)) return;

      const quantity = parseInt(radio.value, 10);
      if (Number.isNaN(quantity)) return;

      this.updateFormQuantity(quantity);
      this.updateSelectedState();
      this.scheduleSyncAtcPrice();
    }

    updateFormQuantity(quantity) {
      try {
        if (this.sectionId && !this.sectionId.includes('{')) {
          const quantityInput = document.querySelector(`#Quantity-${this.sectionId}`);
          if (quantityInput) {
            quantityInput.value = quantity;
            quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        if (this.formId && !this.formId.includes('{')) {
          const form = document.getElementById(this.formId);
          if (form) {
            let hiddenQty = form.querySelector('input[name="quantity"]');
            if (!hiddenQty) {
              hiddenQty = document.createElement('input');
              hiddenQty.type = 'hidden';
              hiddenQty.name = 'quantity';
              form.appendChild(hiddenQty);
            }
            hiddenQty.value = quantity;
          }
        }

        const quantityInputs = document.querySelectorAll('input[name="quantity"]');
        quantityInputs.forEach((input) => {
          input.value = quantity;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        const productForms = document.querySelectorAll('[data-type="add-to-cart-form"]');
        productForms.forEach((form) => {
          let hiddenQty = form.querySelector('input[name="quantity"]');
          if (!hiddenQty) {
            hiddenQty = document.createElement('input');
            hiddenQty.type = 'hidden';
            hiddenQty.name = 'quantity';
            form.appendChild(hiddenQty);
          }
          hiddenQty.value = quantity;
        });
      } catch (e) {
        // Ignore errors from template placeholders
      }
    }

    updateSelectedState() {
      this.querySelectorAll('.quantity-bundle__option').forEach((option) => {
        const radio = option.querySelector('.quantity-bundle__radio');
        if (radio && radio.checked) {
          option.classList.add('quantity-bundle__option--selected');
        } else {
          option.classList.remove('quantity-bundle__option--selected');
        }
      });
    }
  }

  customElements.define('quantity-bundle-selector', QuantityBundleSelector);
}
